/**
 * Utility functions for working with arXiv URLs and IDs
 */

/**
 * Normalizes arXiv URLs to handle both arxiv.org and www.arxiv.org
 */
function normalizeArxivHostname(url: URL): boolean {
  return url.hostname === 'arxiv.org' || url.hostname === 'www.arxiv.org';
}

/**
 * Converts an arXiv abstract URL to a PDF URL
 * Example: https://arxiv.org/abs/2301.12345 -> https://arxiv.org/pdf/2301.12345.pdf
 */
export function convertArxivUrlToPdf(arxivUrl: string): string {
  try {
    const url = new URL(arxivUrl);
    
    // Handle different arXiv URL formats (both arxiv.org and www.arxiv.org)
    if (normalizeArxivHostname(url)) {
      // Extract the arXiv ID from the path
      const pathMatch = url.pathname.match(/\/abs\/(.+)$/);
      if (pathMatch && pathMatch[1]) {
        const arxivId = pathMatch[1];
        // Always use canonical URL without www
        return `https://arxiv.org/pdf/${arxivId}.pdf`;
      }
    }
    
    // If it's already a PDF URL, return as-is
    if (arxivUrl.includes('/pdf/') && arxivUrl.endsWith('.pdf')) {
      return arxivUrl;
    }
    
    // Fallback: return the original URL
    return arxivUrl;
  } catch (error) {
    console.error('Error converting arXiv URL to PDF:', error);
    return arxivUrl;
  }
}

/**
 * Extracts arXiv ID from various arXiv URL formats
 * Example: https://arxiv.org/abs/2301.12345 -> 2301.12345
 */
export function extractArxivId(arxivUrl: string): string | null {
  try {
    const url = new URL(arxivUrl);
    
    if (normalizeArxivHostname(url)) {
      // Handle /abs/ URLs
      const absMatch = url.pathname.match(/\/abs\/(.+)$/);
      if (absMatch && absMatch[1]) {
        return absMatch[1];
      }
      
      // Handle /pdf/ URLs
      const pdfMatch = url.pathname.match(/\/pdf\/(.+)\.pdf$/);
      if (pdfMatch && pdfMatch[1]) {
        return pdfMatch[1];
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error extracting arXiv ID:', error);
    return null;
  }
}

/**
 * Checks if a URL is an arXiv URL
 */
export function isArxivUrl(url: string): boolean {
  try {
    const urlObj = new URL(url);
    return normalizeArxivHostname(urlObj) && 
           (urlObj.pathname.includes('/abs/') || urlObj.pathname.includes('/pdf/'));
  } catch (error) {
    return false;
  }
}